import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, StarOff, Loader2, AlertTriangle, Waves, TrendingUp, TrendingDown, Calculator, NotebookPen, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import client from "../lib/api";
import { GradeBadge, ActionBadge, GateBar, SubScore, EarningsDot } from "../components/Badges";
import { num } from "../lib/format";

export default function StockDetail() {
  const { ticker } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [direction, setDirection] = useState(sp.get("direction") || "long");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [watched, setWatched] = useState(false);
  const [settings, setSettings] = useState({ account_size: 25000, risk_pct: 1.0 });
  const [signals, setSignals] = useState(null);
  const [prob, setProb] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/stock/${ticker}`, { params: { direction } });
      setData(data);
      if (data.earnings_level === "blocked" || data.earnings_level === "today")
        toast.error(`${ticker}: earnings imminent — trade blocked`);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [ticker, direction]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setSignals(null);
    client.get(`/stock/${ticker}/signals`, { params: { direction, hold: 10 } })
      .then(({ data }) => setSignals(data)).catch(() => setSignals({ summary: { signals: 0 } }));
  }, [ticker, direction]);
  useEffect(() => {
    setProb(null);
    client.get(`/stock/${ticker}/probability`, { params: { direction } })
      .then(({ data }) => setProb(data)).catch(() => setProb({ available: false }));
  }, [ticker, direction]);
  useEffect(() => {
    client.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const saveSettings = async (next) => {
    setSettings(next);
    try {
      const { data } = await client.put("/settings", next);
      setSettings(data);
    } catch { /* noop */ }
  };
  useEffect(() => {
    client.get("/watchlist", { params: { direction } })
      .then(({ data }) => setWatched((data.tickers || []).includes(ticker)))
      .catch(() => {});
  }, [ticker, direction]);

  const toggleWatch = async () => {
    if (watched) {
      await client.delete(`/watchlist/${ticker}`);
      setWatched(false);
      toast("Removed from watchlist");
    } else {
      await client.post("/watchlist", { ticker });
      setWatched(true);
      toast.success(`${ticker} promoted to watchlist`);
    }
  };

  if (loading)
    return <div className="flex h-[70vh] items-center justify-center"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  if (!data)
    return <div className="p-10 text-center text-gray-500">No data for {ticker}.</div>;

  const t = data.trade;

  return (
    <div className="px-4 py-5 sm:px-6 sg-fade">
      <button data-testid="back-btn" onClick={() => nav(-1)} className="mb-4 flex items-center gap-1.5 text-xs text-gray-500 transition-colors duration-200 hover:text-white">
        <ArrowLeft size={14} /> Back to scanner
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-white/10 pb-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-head text-3xl font-black tracking-tight" data-testid="detail-ticker">{data.ticker}</h1>
            <GradeBadge grade={data.grade} gradeKey={data.grade_key} score={data.score} />
          </div>
          <p className="mt-1 text-sm text-gray-500">{data.name} · {data.sector} · RS vs {data.sector_etf}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-sm border border-white/10">
            <button data-testid="detail-long" onClick={() => setDirection("long")} className={`flex items-center gap-1 px-3 py-1.5 font-mono-sg text-xs font-bold ${direction === "long" ? "bg-white/10 text-white" : "text-gray-500"}`}><TrendingUp size={13} />LONG</button>
            <button data-testid="detail-short" onClick={() => setDirection("short")} className={`flex items-center gap-1 px-3 py-1.5 font-mono-sg text-xs font-bold ${direction === "short" ? "bg-white/10 text-white" : "text-gray-500"}`}><TrendingDown size={13} />SHORT</button>
          </div>
          <button data-testid="watch-toggle" onClick={toggleWatch}
            className={`flex items-center gap-1.5 rounded-sm border px-3 py-1.5 text-xs transition-colors duration-200 ${watched ? "border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700]" : "border-white/10 text-gray-400 hover:border-white/20 hover:text-white"}`}>
            {watched ? <Star size={14} fill="currentColor" /> : <StarOff size={14} />} {watched ? "Watching" : "Watch"}
          </button>
        </div>
      </div>

      {/* Status banner */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <ActionBadge action={data.action} size="lg" />
        {data.signal_status && (
          <span data-testid="signal-status" className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 font-mono-sg text-xs font-bold ${
            data.signal_status === "ACTIVE" ? "border-[#00FF66]/40 bg-[#00FF66]/10 text-[#00FF66]"
            : data.signal_status === "EXPIRING" ? "border-[#FF7F00]/40 bg-[#FF7F00]/10 text-[#FF7F00]"
            : "border-gray-700 bg-white/5 text-gray-500"}`}>
            {data.signal_status === "ACTIVE" ? "SIGNAL ACTIVE"
              : data.signal_status === "EXPIRING" ? `EXPIRES IN ${data.expires_in}D`
              : "SIGNAL EXPIRED"}
          </span>
        )}
        <EarningsDot level={data.earnings_level} />
        {data.live && (
          <span className="inline-flex items-center gap-1.5 rounded-sm border border-[#00FF66]/40 bg-[#00FF66]/10 px-2 py-0.5 font-mono-sg text-xs font-bold text-[#00FF66]" data-testid="live-badge">
            <span className="h-2 w-2 rounded-full bg-[#00FF66] sg-blink" /> LIVE ${num(data.live.price)}
          </span>
        )}
        <span className="font-mono-sg text-xs text-gray-500">Price <span className="text-white">${num(data.price)}</span></span>
        <span className="font-mono-sg text-xs text-gray-500">RVOL <span className="text-white">{num(data.live?.rvol ?? data.rvol)}</span></span>
        <span className="font-mono-sg text-xs text-gray-500">ADX <span className="text-white">{num(data.adx, 1)}</span></span>
        <span className="font-mono-sg text-xs text-gray-500">Weekly <span className={data.weekly_uptrend ? "text-[#00FF66]" : "text-[#FF7F00]"}>{data.weekly_uptrend ? "UP" : "MIXED"}</span></span>
      </div>

      {(data.hard_fails.length > 0 || data.is_chop) && (
        <div className="mt-4 space-y-2">
          {data.is_chop && (
            <div data-testid="chop-banner" className="flex items-start gap-2 border border-gray-600 bg-white/5 px-3 py-2 text-sm text-gray-300">
              <Waves size={16} className="mt-0.5 shrink-0 text-gray-400" />
              <div><span className="font-bold">NO TRADE — CHOP.</span> {data.chop_reasons.join(" · ")}</div>
            </div>
          )}
          {data.hard_fails.length > 0 && (
            <div data-testid="hardfail-banner" className="flex items-start gap-2 border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2 text-sm text-[#FF3B30]">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div><span className="font-bold">Hard-fail override.</span> {data.hard_fails.join(" · ")}</div>
            </div>
          )}
        </div>
      )}

      <ProbabilityCard prob={prob} score={data.score} setupType={data.setup_type} />

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {/* Gates */}
        <div className="lg:col-span-2 border border-white/10 bg-[#111111] p-5">
          <h2 className="font-head text-lg font-bold">7-Gate Breakdown</h2>
          <p className="text-xs text-gray-500">Weighted score, max 100. Overrides can block a high score.</p>
          <div className="mt-3 divide-y divide-white/5">
            {data.gates.map((g) => <GateBar key={g.label} {...g} />)}
          </div>
        </div>

        {/* Right column: sub-scores + trade plan */}
        <div className="space-y-4">
          <div className="border border-white/10 bg-[#111111] p-5">
            <h2 className="font-head text-lg font-bold">Trade Quality</h2>
            <div className="mt-3 space-y-3">
              <SubScore label="Trend" value={data.trade_quality.trend} />
              <SubScore label="Rel. Strength" value={data.trade_quality.rs} />
              <SubScore label="Momentum" value={data.trade_quality.momentum} />
              <SubScore label="Volume" value={data.trade_quality.volume} />
            </div>
          </div>

          <div className="border border-white/10 bg-[#111111] p-5">
            <div className="flex items-baseline justify-between">
              <h2 className="font-head text-lg font-bold">Trade Plan</h2>
              <span className="font-mono-sg text-[10px] uppercase tracking-wide" style={{ color: t.basis === "live" ? "#00FF66" : "#666" }}>
                {t.basis === "live" ? "live price" : "EOD close"}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 font-mono-sg text-sm">
              <Stat label="Entry" value={`$${num(t.entry)}`} />
              <Stat label="Stop" value={`$${num(t.stop)}`} color="#FF3B30" />
              <Stat label="Target 1" value={`$${num(t.target1)}`} color="#00FF66" />
              <Stat label="Target 2" value={`$${num(t.target2)}`} color="#00FF66" />
              <Stat label="R:R" value={`${num(t.rr)}`} color={t.rr >= 2 ? "#00FF66" : "#FF7F00"} />
              <Stat label="Risk" value={`${num(t.risk_pct)}%`} />
              <Stat label="ATR" value={`$${num(t.atr)}`} />
              <Stat label="RS Rating" value={data.rs.rating} />
            </div>
          </div>

          <PositionSize trade={t} settings={settings} onSave={saveSettings} ticker={data.ticker} direction={direction} />
        </div>
      </div>

      {/* Chart */}
      {data.chart && (
        <div className="mt-4 border border-white/10 bg-[#111111] p-5">
          <h2 className="font-head text-lg font-bold">Price · 120d (Close / 20EMA / 50SMA / 200SMA)</h2>
          <div className="mt-4 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.chart}>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="date" stroke="#444" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} minTickGap={40} />
                <YAxis stroke="#444" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} width={48}
                  domain={["dataMin", "dataMax"]} allowDataOverflow={false} />
                <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <Line type="monotone" dataKey="close" stroke="#00F0FF" dot={false} strokeWidth={1.8} name="Close" />
                <Line type="monotone" dataKey="ema20" stroke="#FFD700" dot={false} strokeWidth={1} name="20EMA" />
                <Line type="monotone" dataKey="sma50" stroke="#FF7F00" dot={false} strokeWidth={1} name="50SMA" />
                <Line type="monotone" dataKey="sma200" stroke="#888" dot={false} strokeWidth={1} name="200SMA" />
                <ReferenceLine y={t.entry} stroke="#00F0FF" strokeDasharray="5 4" ifOverflow="extendDomain" label={{ value: `Entry ${num(t.entry)}`, position: "insideLeft", fill: "#00F0FF", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <ReferenceLine y={t.stop} stroke="#FF3B30" strokeDasharray="5 4" ifOverflow="extendDomain" label={{ value: `Stop ${num(t.stop)}`, position: "insideRight", fill: "#FF3B30", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <ReferenceLine y={t.target1} stroke="#00FF66" strokeDasharray="5 4" ifOverflow="extendDomain" label={{ value: `T1 ${num(t.target1)}`, position: "insideTopLeft", fill: "#00FF66", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <ReferenceLine y={t.target2} stroke="#00FF66" strokeOpacity={0.5} strokeDasharray="3 5" ifOverflow="extendDomain" label={{ value: `T2 ${num(t.target2)}`, position: "insideTopRight", fill: "#00FF66", fontSize: 10, fontFamily: "JetBrains Mono" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <SignalHistory data={signals} ticker={data.ticker} />

      <div className="mt-4 flex justify-end">
        <button data-testid="detail-backtest-btn" onClick={() => nav(`/backtest?ticker=${data.ticker}&direction=${direction}`)}
          className="rounded-sm border border-white/10 px-4 py-2 font-mono-sg text-xs text-gray-300 transition-colors duration-200 hover:border-[#00F0FF]/40 hover:text-[#00F0FF]">
          Backtest {data.ticker} rules →
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-white/5 bg-black/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-gray-600">{label}</div>
      <div className="mt-0.5 font-bold" style={{ color: color || "#fff" }}>{value}</div>
    </div>
  );
}

function ProbabilityCard({ prob, score, setupType }) {
  if (!prob) {
    return (
      <div className="mt-6 flex items-center justify-center gap-2 border border-white/10 bg-[#111111] py-8 text-sm text-gray-500" data-testid="probability-loading">
        <Loader2 size={16} className="animate-spin text-[#00F0FF]" /> Loading historical probability…
      </div>
    );
  }
  if (!prob.available) {
    return (
      <div className="mt-6 border border-white/10 bg-[#111111] p-5" data-testid="probability-unavailable">
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="text-[#00F0FF]" />
          <h2 className="font-head text-lg font-bold">Historical Probability</h2>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          {prob.building ? "Building the historical signal model — check back shortly." : "Historical model not available yet."}
        </p>
      </div>
    );
  }

  const CONF = {
    High: { cls: "text-[#00FF66] border-[#00FF66]/40 bg-[#00FF66]/10" },
    Medium: { cls: "text-[#FFD700] border-[#FFD700]/40 bg-[#FFD700]/10" },
    Low: { cls: "text-[#FF7F00] border-[#FF7F00]/40 bg-[#FF7F00]/10" },
  };
  const conf = CONF[prob.confidence] || CONF.Low;
  const h = prob.headline || {};
  const insufficient = prob.n < 50;

  return (
    <div className="mt-6 border border-[#00F0FF]/20 bg-[#0c0f10] p-5" data-testid="probability-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="text-[#00F0FF]" />
          <h2 className="font-head text-lg font-bold">Historical Probability</h2>
        </div>
        <span data-testid="probability-confidence" className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 font-mono-sg text-xs font-bold ${conf.cls}`}>
          {prob.confidence.toUpperCase()} CONFIDENCE · {prob.tier}
        </span>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Calibrated from <span className="text-gray-300">{prob.n}</span> comparable historical setups
        (matched on {prob.match_label}). This is an outcome estimate — <span className="text-gray-300">separate from the Technical Score</span>.
      </p>

      {/* Headline split: quality (score) vs outcome (probability) */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 font-mono-sg">
        <div className="border border-white/5 bg-black/40 px-3 py-2.5" data-testid="prob-technical-score">
          <div className="text-[10px] uppercase tracking-wide text-gray-600">Technical Score</div>
          <div className="mt-0.5 text-2xl font-black text-white">{score}<span className="text-sm text-gray-600">/100</span></div>
          <div className="text-[10px] text-gray-600">setup quality</div>
        </div>
        <div className="border border-[#00F0FF]/30 bg-[#00F0FF]/5 px-3 py-2.5" data-testid="prob-hist-probability">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Hist. Probability</div>
          <div className="mt-0.5 text-2xl font-black text-[#00F0FF]">{num(h.prob_1r, 1)}%</div>
          <div className="text-[10px] text-gray-600">hits +1R by day {prob.primary_horizon}</div>
        </div>
        <div className="border border-white/5 bg-black/40 px-3 py-2.5" data-testid="prob-expected-r">
          <div className="text-[10px] uppercase tracking-wide text-gray-600">Expected R</div>
          <div className="mt-0.5 text-2xl font-black" style={{ color: (h.exp_r || 0) > 0 ? "#00FF66" : "#FF3B30" }}>
            {(h.exp_r || 0) > 0 ? "+" : ""}{num(h.exp_r, 2)}R
          </div>
          <div className="text-[10px] text-gray-600">avg outcome, day {prob.primary_horizon}</div>
        </div>
        <div className="border border-white/5 bg-black/40 px-3 py-2.5" data-testid="prob-setup-type">
          <div className="text-[10px] uppercase tracking-wide text-gray-600">Setup Type</div>
          <div className="mt-0.5 text-sm font-bold text-[#FFD700] leading-tight">{setupType || prob.setup_type}</div>
          <div className="text-[10px] text-gray-600">closes up {num(h.prob_up, 1)}%</div>
        </div>
      </div>

      {insufficient && (
        <p className="mt-3 text-[11px] text-[#FF7F00]" data-testid="prob-insufficient">
          Insufficient comparable sample ({prob.n} &lt; 50). Treat these odds as indicative only.
        </p>
      )}

      {/* Horizon table */}
      <div className="mt-4 overflow-x-auto border border-white/10" data-testid="prob-horizon-table">
        <table className="w-full min-w-[420px] font-mono-sg">
          <thead className="bg-[#111111]">
            <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-3 py-2">Horizon</th>
              <th className="px-3 py-2 text-right">Closes Up</th>
              <th className="px-3 py-2 text-right">+1R</th>
              <th className="px-3 py-2 text-right">+2R</th>
              <th className="px-3 py-2 text-right">Exp R</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {(prob.horizons || []).map((row) => (
              <tr key={row.day} className={`border-t border-white/5 ${row.day === prob.primary_horizon ? "bg-[#00F0FF]/5" : ""}`}>
                <td className="px-3 py-2 text-gray-300">Day {row.day}</td>
                <td className="px-3 py-2 text-right text-gray-300">{num(row.prob_up, 1)}%</td>
                <td className="px-3 py-2 text-right text-[#00FF66]">{num(row.prob_1r, 1)}%</td>
                <td className="px-3 py-2 text-right text-[#00FF66]/70">{num(row.prob_2r, 1)}%</td>
                <td className="px-3 py-2 text-right font-bold" style={{ color: row.exp_r > 0 ? "#00FF66" : "#FF3B30" }}>
                  {row.exp_r > 0 ? "+" : ""}{num(row.exp_r, 2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-gray-600">
        The Technical Score grades setup quality; it is never a probability of profit. Historical Probability estimates
        outcome odds from similar past setups (no look-ahead) and is not a guarantee. Optimised for 2–10 trading-day holds.
      </p>
    </div>
  );
}

function SignalHistory({ data, ticker }) {
  if (!data) {
    return (
      <div className="mt-4 flex items-center justify-center gap-2 border border-white/10 bg-[#111111] py-10 text-sm text-gray-500" data-testid="signal-history-loading">
        <Loader2 size={16} className="animate-spin text-[#00F0FF]" /> Simulating historical signals…
      </div>
    );
  }
  const s = data.summary;
  if (!s || !s.signals) {
    return (
      <div className="mt-4 border border-white/10 bg-[#111111] p-5" data-testid="signal-history-empty">
        <h2 className="font-head text-lg font-bold">Signal History</h2>
        <p className="mt-2 text-sm text-gray-500">No qualifying signals for {ticker} in the 2-year lookback (this direction).</p>
      </div>
    );
  }
  const OUT = {
    target: { cls: "text-[#00FF66] border-[#00FF66]/40 bg-[#00FF66]/10", label: "TARGET" },
    stop: { cls: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10", label: "STOP" },
    time: { cls: "text-gray-400 border-gray-600 bg-white/5", label: "TIME EXIT" },
  };
  return (
    <div className="mt-4 border border-white/10 bg-[#111111] p-5" data-testid="signal-history">
      <div className="flex items-baseline justify-between">
        <h2 className="font-head text-lg font-bold">Signal History</h2>
        <span className="font-mono-sg text-[10px] uppercase tracking-wide text-gray-600">hypothetical · 10-day max hold · 2y</span>
      </div>
      <p className="mt-1 text-xs text-gray-500">Every time this setup triggered historically — did price hit target or stop first, and how long it held.</p>

      <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-6 font-mono-sg text-sm">
        <Stat label="Signals" value={s.signals} />
        <Stat label="Win Rate" value={`${s.win_rate}%`} color={s.win_rate >= 50 ? "#00FF66" : "#FF7F00"} />
        <Stat label="Expectancy" value={`${s.expectancy}R`} color={s.expectancy > 0 ? "#00FF66" : "#FF3B30"} />
        <Stat label="Avg Hold" value={`${s.avg_hold}d`} />
        <Stat label="Targets / Stops" value={`${s.target_hits} / ${s.stop_hits}`} />
        <Stat label="Best / Worst" value={`${s.best_r} / ${s.worst_r}R`} />
      </div>

      <div className="mt-4 max-h-72 overflow-auto border border-white/10">
        <table className="w-full" data-testid="signal-history-table">
          <thead className="sticky top-0 bg-[#111111]">
            <tr className="text-left font-mono-sg text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-3 py-2">Signal Date</th>
              <th className="px-3 py-2">Setup</th>
              <th className="px-3 py-2 text-right">Entry</th>
              <th className="px-3 py-2 text-right">Exit</th>
              <th className="px-3 py-2">Outcome</th>
              <th className="px-3 py-2 text-right">R</th>
              <th className="px-3 py-2 text-right">Held</th>
            </tr>
          </thead>
          <tbody className="font-mono-sg text-sm">
            {data.recent.map((t, i) => {
              const o = OUT[t.outcome] || OUT.time;
              return (
                <tr key={i} className="border-t border-white/5">
                  <td className="px-3 py-2 text-gray-300">{t.signal_date}</td>
                  <td className="px-3 py-2 text-gray-500">{t.entry_state}</td>
                  <td className="px-3 py-2 text-right text-gray-300">${num(t.entry)}</td>
                  <td className="px-3 py-2 text-right text-gray-300">${num(t.exit)}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-bold ${o.cls}`}>{o.label}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-bold" style={{ color: t.r > 0 ? "#00FF66" : "#FF3B30" }}>{t.r > 0 ? "+" : ""}{num(t.r)}</td>
                  <td className="px-3 py-2 text-right text-gray-400">{t.days_held}d</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PositionSize({ trade, settings, onSave, ticker, direction }) {
  const [acctD, setAcctD] = useState(String(settings.account_size));
  const [riskD, setRiskD] = useState(String(settings.risk_pct));
  const [logging, setLogging] = useState(false);
  useEffect(() => {
    setAcctD(String(settings.account_size));
    setRiskD(String(settings.risk_pct));
  }, [settings.account_size, settings.risk_pct]);

  const acct = Number(acctD) || 0;
  const riskPct = Number(riskD) || 0;
  const riskPerShare = Math.abs((trade.entry || 0) - (trade.stop || 0));
  const dollarRisk = acct * (riskPct / 100);
  const shares = riskPerShare > 0 ? Math.floor(dollarRisk / riskPerShare) : 0;
  const positionValue = shares * (trade.entry || 0);
  const pctAccount = acct > 0 ? (positionValue / acct) * 100 : 0;

  const commit = () => {
    const a = Math.max(1, Number(acctD) || 0);
    const r = Math.min(100, Math.max(0.05, Number(riskD) || 0));
    onSave({ account_size: a, risk_pct: r });
  };

  const logTrade = async () => {
    if (shares <= 0) { toast.error("Set a valid stop to size the trade"); return; }
    setLogging(true);
    try {
      await client.post("/trades", {
        ticker, direction, entry_price: trade.entry, stop: trade.stop,
        target: trade.target1, shares,
      });
      toast.success(`Logged ${shares} ${ticker} @ $${num(trade.entry)}`);
    } catch { toast.error("Could not log trade"); }
    finally { setLogging(false); }
  };

  return (
    <div className="border border-white/10 bg-[#111111] p-5" data-testid="position-size-card">
      <div className="flex items-center gap-2">
        <Calculator size={16} className="text-[#00F0FF]" />
        <h2 className="font-head text-lg font-bold">Position Size</h2>
      </div>
      <p className="mt-1 text-xs text-gray-500">Suggested share size from your account risk.</p>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1 block text-[10px] uppercase tracking-wide text-gray-600">Account $</span>
          <input data-testid="account-size-input" type="number" min="1" value={acctD}
            onChange={(e) => setAcctD(e.target.value)} onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            className="w-full rounded-sm border border-white/10 bg-transparent px-2 py-1.5 font-mono-sg text-sm text-white outline-none focus:border-[#00F0FF]" />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] uppercase tracking-wide text-gray-600">Risk % / trade</span>
          <input data-testid="risk-pct-input" type="number" min="0.05" max="100" step="0.25" value={riskD}
            onChange={(e) => setRiskD(e.target.value)} onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            className="w-full rounded-sm border border-white/10 bg-transparent px-2 py-1.5 font-mono-sg text-sm text-white outline-none focus:border-[#00F0FF]" />
        </label>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 font-mono-sg text-sm">
        <Stat label="Risk $" value={`$${dollarRisk.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} color="#FF3B30" />
        <Stat label="Risk / Share" value={`$${num(riskPerShare)}`} />
        <div className="col-span-2 border border-[#00F0FF]/30 bg-[#00F0FF]/5 px-3 py-2.5" data-testid="suggested-shares">
          <div className="text-[10px] uppercase tracking-wide text-gray-500">Suggested Shares</div>
          <div className="mt-0.5 font-mono-sg text-2xl font-black text-[#00F0FF]">{shares.toLocaleString()}</div>
        </div>
        <Stat label="Position $" value={`$${positionValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
        <Stat label="% of Account" value={`${num(pctAccount, 1)}%`} color={pctAccount > 100 ? "#FF7F00" : "#fff"} />
      </div>
      {pctAccount > 100 && (
        <p className="mt-2 text-[11px] text-[#FF7F00]">Position exceeds account — would require margin.</p>
      )}
      <button data-testid="log-trade-btn" onClick={logTrade} disabled={logging}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-sm bg-[#00F0FF] py-2.5 font-mono-sg text-sm font-bold text-black transition-colors duration-200 hover:bg-[#00F0FF]/80 disabled:opacity-50">
        {logging ? <Loader2 size={15} className="animate-spin" /> : <NotebookPen size={15} />} LOG THIS TRADE
      </button>
    </div>
  );
}
